// src/contexts/ThemeContext.jsx
// Provides `theme`, `toggleTheme`, and `setTheme` to the entire app.
// Persists the user's choice to localStorage.
// Detects system preference (prefers-color-scheme) on first visit.

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

const STORAGE_KEY = 'jmi-theme';
const THEMES = { LIGHT: 'light', DARK: 'dark' };

/* ── helpers ─────────────────────────────────────────────────────────────── */
function getSystemTheme() {
  if (typeof window === 'undefined') return THEMES.LIGHT;
  return window.matchMedia('(prefers-color-scheme: dark)').matches
    ? THEMES.DARK
    : THEMES.LIGHT;
}

function getSavedTheme() {
  try {
    return localStorage.getItem(STORAGE_KEY) || null;
  } catch {
    return null;
  }
}

function applyTheme(theme) {
  const root = document.documentElement;           // <html>
  root.setAttribute('data-theme', theme);
  // Also set color-scheme for native browser widgets (scrollbars, inputs…)
  root.style.colorScheme = theme;
}

/* ── context ─────────────────────────────────────────────────────────────── */
const ThemeContext = createContext({
  theme:       THEMES.LIGHT,
  isDark:      false,
  toggleTheme: () => {},
  setTheme:    () => {},
});

/* ── provider ────────────────────────────────────────────────────────────── */
export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => {
    // 1st: user's saved preference
    const saved = getSavedTheme();
    if (saved === THEMES.LIGHT || saved === THEMES.DARK) return saved;
    // 2nd: system preference
    return getSystemTheme();
  });

// Apply theme to <html> whenever it changes
   useEffect(() => {
     applyTheme(theme);
     // Force a reflow to help Capacitor web view update
     const root = document.documentElement;
     void root.offsetWidth;
     try {
       localStorage.setItem(STORAGE_KEY, theme);
     } catch {
       // storage might be blocked in private/incognito; gracefully ignore
     }
   }, [theme]);

  // Listen for system preference changes (if user hasn't saved a preference)
  useEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => {
      const saved = getSavedTheme();
      // Only auto-switch if user has never set a manual preference
      if (!saved) {
        setThemeState(e.matches ? THEMES.DARK : THEMES.LIGHT);
      }
    };
    media.addEventListener('change', handleChange);
    return () => media.removeEventListener('change', handleChange);
  }, []);

  const setTheme = useCallback((newTheme) => {
    if (newTheme === THEMES.LIGHT || newTheme === THEMES.DARK) {
      setThemeState(newTheme);
    }
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((prev) => (prev === THEMES.DARK ? THEMES.LIGHT : THEMES.DARK));
  }, []);

  return (
    <ThemeContext.Provider
      value={{
        theme,
        isDark:      theme === THEMES.DARK,
        isLight:     theme === THEMES.LIGHT,
        toggleTheme,
        setTheme,
        THEMES,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
}

/* ── hook ────────────────────────────────────────────────────────────────── */
export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme must be used inside <ThemeProvider>');
  }
  return ctx;
}

export default ThemeContext;
